/**
 * What a student sees of a module's task list: each assessment and
 * assignment as its own row with their progress, like a module's session
 * recordings. A cohort's tasks come from the batch's module; a 1:1 student's
 * from their own. Data: GET api/module-tasks/student/course/<course>/.
 */

import React from "react";
import { Box, Chip, Typography } from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import TaskAltIcon from "@mui/icons-material/TaskAlt";

import api from "../../api";

const FONT = "Be Vietnam Pro";

export const getStudentTasks = async (courseId) =>
  (await api.get(`api/module-tasks/student/course/${courseId}/`)).data.modules;

const assessmentDone = (a) => a.status === "completed";
const assignmentDone = (a) => a.status === "submitted" || a.status === "reviewed";

/** How many of a module's tasks the student still has to do. */
export const tasksToDo = (tasks) =>
  tasks
    ? tasks.assessments.filter((a) => !assessmentDone(a)).length
      + tasks.assignments.filter((a) => !assignmentDone(a)).length
    : 0;

/** A chip for the module card's header: "3 to do", or "Tasks done". */
export const TasksChip = ({ tasks }) => {
  const total = tasks ? tasks.assessments.length + tasks.assignments.length : 0;
  if (!total) return null;
  const todo = tasksToDo(tasks);
  return (
    <Chip
      size="small"
      icon={todo ? <PlayCircleOutlineIcon /> : <TaskAltIcon />}
      label={todo ? `${todo} task${todo === 1 ? "" : "s"} to do` : "Tasks done"}
      color={todo ? "warning" : "success"}
      variant="outlined"
      sx={{ fontFamily: FONT, height: 28, borderRadius: "8px" }}
      data-testid="tasks-chip"
    />
  );
};

const ASSESSMENT_STATUS = {
  not_started: { label: "Not started", icon: <RadioButtonUncheckedIcon fontSize="inherit" />, color: "#6b7280" },
  in_progress: { label: "In progress", icon: <PlayCircleOutlineIcon fontSize="inherit" />, color: "#0f6ca8" },
  completed: { label: "Completed", icon: <CheckCircleOutlineIcon fontSize="inherit" />, color: "#1f7a45" },
};
const ASSIGNMENT_STATUS = {
  not_submitted: { label: "Not submitted", icon: <RadioButtonUncheckedIcon fontSize="inherit" />, color: "#6b7280" },
  submitted: { label: "Submitted · awaiting review", icon: <HourglassEmptyIcon fontSize="inherit" />, color: "#b07d1a" },
  reviewed: { label: "Reviewed", icon: <CheckCircleOutlineIcon fontSize="inherit" />, color: "#1f7a45" },
  needs_revision: { label: "Needs revision", icon: <AutorenewIcon fontSize="inherit" />, color: "#c2410c" },
};
const TYPE_WORDS = { o: "MCQ", s: "subjective", p: "programming" };

const Row = ({ title, detail, status, onOpen, testId }) => (
  <Box
    role="button" tabIndex={0} data-testid={testId}
    onClick={(e) => { e.stopPropagation(); onOpen(); }}
    onKeyDown={(e) => { if (e.key === "Enter") onOpen(); }}
    sx={{
      display: "flex", alignItems: "center", gap: 1.5, px: 1.5, py: 1, ml: 4, width: "calc(98% - 32px)",
      borderRadius: "8px", border: "1px solid #e4eaf0", background: "#fff", cursor: "pointer", mb: 0.75,
      "&:hover": { borderColor: "#9fcdea", background: "#f5fafd" },
    }}
  >
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography sx={{ fontSize: 14, fontWeight: 500, fontFamily: FONT, color: "#16232e",
                        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        {title}
      </Typography>
      {detail && <Typography sx={{ fontSize: 12, color: "#6b7280" }}>{detail}</Typography>}
    </Box>
    <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, fontSize: 12, color: status.color,
                                whiteSpace: "nowrap" }}>
      {status.icon} {status.label}
    </Box>
    <ChevronRightIcon sx={{ color: "#9ca3af" }} />
  </Box>
);

export const AssessmentList = ({ assessments, onOpen }) => {
  if (!assessments?.length) {
    return <Typography sx={{ ml: 4, mb: 1, fontSize: 13, color: "#6b7280" }}>No assessment for this module yet.</Typography>;
  }
  return assessments.map((a) => {
    const parts = Object.entries(a.counts || {}).filter(([, n]) => n).map(([t, n]) => `${n} ${TYPE_WORDS[t]}`);
    return (
      <Row key={a.id ?? "other"} testId="student-assessment" title={a.title} onOpen={() => onOpen(a)}
           status={ASSESSMENT_STATUS[a.status] || ASSESSMENT_STATUS.not_started}
           detail={`${a.answered}/${a.question_count} answered${parts.length ? ` · ${parts.join(", ")}` : ""}`} />
    );
  });
};

export const AssignmentList = ({ assignments, onOpen }) => {
  if (!assignments?.length) {
    return <Typography sx={{ ml: 4, mb: 1, fontSize: 13, color: "#6b7280" }}>No assignment for this module yet.</Typography>;
  }
  return assignments.map((a) => (
    <Row key={a.id} testId="student-assignment" title={a.title} onOpen={() => onOpen(a)}
         status={ASSIGNMENT_STATUS[a.status] || ASSIGNMENT_STATUS.not_submitted}
         detail={a.score !== null && a.score !== undefined ? `Score ${a.score}/100${a.feedback ? " · feedback from your mentor" : ""}`
           : a.feedback ? "Feedback from your mentor" : null} />
  ));
};
